import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, Lock, User } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nProvider";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errorKey } from "@/lib/api";
import { FullScreenLoader } from "@/components/FullScreenLoader";

export default function Login() {
  const { t } = useI18n();
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <FullScreenLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) return setError("validation.required");
    setBusy(true);
    setError("");
    try {
      const u = await login(identifier.trim(), password);
      const from = location.state?.from;
      const home = homeFor(u);
      navigate(from && from.startsWith(home) ? from : home, { replace: true });
    } catch (err) {
      setError(errorKey(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[100dvh] bg-[#F8FAFC] lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden overflow-hidden bg-[#000B45] lg:flex lg:flex-col lg:justify-between lg:p-14">
        <div className="hazard-stripe absolute inset-x-0 top-0 h-2" />
        <Logo size="xl" testId="login-hero-logo" className="-ml-4" />
        <div className="max-w-md">
          <div className="mb-5 h-1 w-14 rounded-full bg-[#FEB601]" />
          <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-white">{t("app.product")}</h2>
          <p className="mt-3 text-base text-white/70">{t("app.tagline")}</p>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/40">Florida · USA</p>
      </section>

      <section className="flex flex-col px-5 py-6 sm:px-10">
        <div className="flex justify-end">
          <LanguageToggle testIdPrefix="login-lang" />
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center py-10">
          <div className="mb-8 flex justify-center lg:hidden">
            <Logo size="lg" testId="login-logo" className="rounded-xl shadow-md" />
          </div>
          <h1 data-testid="login-title" className="font-display text-3xl font-extrabold tracking-tight text-[#000B45]">{t("auth.title")}</h1>
          <p className="mt-2 text-sm text-gray-600">{t("auth.subtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div className="space-y-2">
              <Label htmlFor="identifier">{t("auth.identifier")}</Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input id="identifier" data-testid="login-identifier-input" autoComplete="username" value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)} className="h-12 bg-white pl-10 text-base" />
              </div>
              <p className="text-xs text-gray-500">{t("auth.identifierHint")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("auth.password")}</Label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <PasswordInput id="password" testId="login-password-input" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-white pl-10 text-base" />
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" data-testid="login-forgot-link"
                  className="rounded px-1 py-1.5 text-sm font-semibold text-[#000B45] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FEB601]">
                  {t("auth.forgot")}
                </Link>
              </div>
            </div>
            {error && (
              <p data-testid="login-error" role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">
                {t(error)}
              </p>
            )}
            <button type="submit" disabled={busy} data-testid="login-submit-button"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#000B45] text-base font-bold tracking-wide text-white transition-colors hover:bg-[#000624] disabled:opacity-70">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? t("auth.submitting") : t("auth.submit")}
            </button>
          </form>
          <InstallAppButton testId="login-install-app" className="mt-4" />
          <p className="mt-6 text-center text-xs text-gray-500">{t("auth.noSignup")}</p>
        </div>
      </section>
    </div>
  );
}
